import { FileArchive, FileCode2, FileSpreadsheet, Search, X } from 'lucide-react';
import { useState } from 'react';

import { formatarBytes } from '@/lib/formato';
import { Badge } from '@/ui/primitives/badge';
import { Input } from '@/ui/primitives/input';

import { BUSCA_A_PARTIR_DE, chaveDoArquivo, tipoDe } from './selecao';

function IconeArquivo({ nome }: { nome: string }) {
  const tipo = tipoDe(nome);
  if (tipo === 'ZIP') return <FileArchive className="size-4 shrink-0 text-marca" aria-hidden />;
  if (tipo === 'XML') return <FileCode2 className="size-4 shrink-0 text-marca" aria-hidden />;
  return <FileSpreadsheet className="size-4 shrink-0 text-sucesso" aria-hidden />;
}

function Resumo({ arquivos }: { arquivos: readonly File[] }) {
  const porTipo = new Map<string, number>();
  for (const a of arquivos) porTipo.set(tipoDe(a.name), (porTipo.get(tipoDe(a.name)) ?? 0) + 1);
  const total = arquivos.reduce((soma, a) => soma + a.size, 0);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-semibold tabular">
        {arquivos.length === 1 ? '1 arquivo' : `${String(arquivos.length)} arquivos`}
      </span>
      <span className="text-sm text-texto-fraco tabular">{formatarBytes(total)}</span>
      {[...porTipo].map(([tipo, n]) => (
        <Badge key={tipo} tom="neutro">
          <span className="tabular">{n}</span> {tipo}
        </Badge>
      ))}
    </div>
  );
}

interface Props {
  arquivos: readonly File[];
  aoTirar: (arquivo: File) => void;
}

/** Arquivos escolhidos: resumo por tipo, busca quando são muitos e lista que rola por dentro. */
export function SelecaoArquivos({ arquivos, aoTirar }: Props) {
  const [busca, setBusca] = useState('');
  const termo = busca.trim().toLowerCase();
  const visiveis = termo ? arquivos.filter((a) => a.name.toLowerCase().includes(termo)) : arquivos;
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Resumo arquivos={arquivos} />
        {arquivos.length >= BUSCA_A_PARTIR_DE && (
          <label className="relative block w-full sm:w-64">
            <span className="sr-only">Buscar nos arquivos escolhidos</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-texto-fraco"
              aria-hidden
            />
            <Input
              type="search"
              className="h-controle-sm pl-9 text-sm"
              placeholder="Buscar pelo nome"
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
              }}
            />
          </label>
        )}
      </div>
      <ul
        className="max-h-64 divide-y divide-borda overflow-y-auto rounded-cartao border border-borda"
        aria-label="Arquivos escolhidos"
      >
        {visiveis.map((arquivo) => (
          <li
            key={chaveDoArquivo(arquivo)}
            className="group flex items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-superficie-hover"
          >
            <IconeArquivo nome={arquivo.name} />
            <span className="min-w-0 flex-1 truncate" title={arquivo.name}>
              {arquivo.name}
            </span>
            <span className="text-xs text-texto-fraco tabular">{formatarBytes(arquivo.size)}</span>
            <button
              type="button"
              aria-label={`Tirar ${arquivo.name}`}
              className="grid size-6 place-items-center rounded-pilula text-texto-fraco transition-colors duration-[var(--duracao-rapida)] hover:bg-superficie-afundada hover:text-texto focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave"
              onClick={() => {
                aoTirar(arquivo);
              }}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </li>
        ))}
        {visiveis.length === 0 && (
          <li className="px-3 py-4 text-center text-sm text-texto-fraco">
            Nenhum arquivo com esse nome.
          </li>
        )}
      </ul>
    </div>
  );
}
